/* eslint-disable */
import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  alpha,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SVG from 'react-inlinesvg'
import { useConfirm, useLocalStorage } from '@/hooks/common'
import colors from '@/styles/colors'
import {
  AccentSlidersData,
  IPatternAsset,
  IPatternAssetColor,
  IPatternAssetMaterial,
  IPatternAssetTemplate,
  PatternSelections,
} from '@/types'
import {
  BoarderTypes,
  BoarderWidthRange,
  MattingWidthRange,
  PatternAssetsMap,
  PatternAssetTabs,
  TemplateCategories,
  TopicType,
} from '@/utils/constants'
import { Assets, TemplateSets } from '@/utils/pattern-assets'
import { Colorize } from '@mui/icons-material'
import AlbumList from './AlbumList'
import ModernPrintTabView from './ModernPrintTabView'
import UploadsList from './UploadsList'
import ModernSettingsTabView from './ModernSettingsTabView'
import ColorPickerDialog from '../ColorPickerDialog'
import { UpdateAddColorHex, SetCurrentCategory, GetSceneObject } from '../renderer/main'
import UpgradeCard from '@/components/UpgradeCard'
import PricingModal from '@/pages/my-profile/components/PricingModal'
import { isDebugMode } from '@/utils/helper'
import { FormLabel, FormSection, StyledSelect } from '@/components/StyledComponents'
import { useSubscription } from '@/hooks/common/useSubscription'

// Free tier limits
const FREE_TIER_LIMITS = {
  material: 15,
  accent: 15,
  print: 12, // For custom prints section
}

// Custom purple theme colors
const PURPLE_THEME = {
  primary: '#8b5cf6', // Main purple
  primaryHover: '#7c3aed', // Darker purple for hover
  primaryLight: '#a78bfa', // Light purple
  primaryBg: '#f3f4f6', // Very light purple background
  primaryBorder: '#e5e7eb', // Light purple border
  text: '#1f2937', // Dark text
  textSecondary: '#6b7280', // Secondary text
  white: '#ffffff',
  background: '#fafafa',
}

// Modern styled components with Figma-level precision
const ModernSidebarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  backgroundColor: PURPLE_THEME.white,
  borderRadius: '0',
  overflow: 'hidden',
}))

const CategoryNavigation = styled(Box)(({ theme }) => ({
  width: '80px',
  backgroundColor: '#f8fafc',
  borderRight: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 0',
  gap: '4px',
  position: 'relative',

  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',

  [theme.breakpoints.down('md')]: {
    width: '64px',
    padding: '12px 0',
  },
}))

const CategoryButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '12px',
  backgroundColor: isActive ? PURPLE_THEME.primary : 'transparent',
  color: isActive ? PURPLE_THEME.white : PURPLE_THEME.textSecondary,
  border: isActive ? 'none' : '1px solid transparent',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    backgroundColor: isActive ? PURPLE_THEME.primaryHover : alpha(PURPLE_THEME.primary, 0.08),
    color: isActive ? PURPLE_THEME.white : PURPLE_THEME.primary,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('md')]: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
  },
}))

const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  fontWeight: 500,
  textAlign: 'center',
  marginTop: '4px',
  lineHeight: 1.2,
  textTransform: 'capitalize',
  color: 'inherit',
}))

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
  position: 'relative',
}))

const ContentHeader = styled(Box)(({ theme }) => ({
  padding: '16px 20px 12px 20px',
  borderBottom: '1px solid #f1f5f9',
  backgroundColor: '#ffffff',
  position: 'relative',
  zIndex: 1,
}))

const ContentTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: '4px',
  textTransform: 'capitalize',
}))

const ContentSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#64748b',
  fontWeight: 400,
}))

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '0', // Remove default padding - individual content will handle their own padding
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: '#cbd5e1',
    },
  },
}))

const AssetGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)', // Fixed 3-column layout
  gap: '12px',
  width: '100%',
  padding: '16px 20px 20px 20px', // Add padding back for asset grids
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)', // Keep 3 columns on mobile too
    gap: '10px',
    padding: '12px 16px 16px 16px',
  },
}))

const MaterialGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)', // Fixed 3-column layout
  gap: '12px',
  width: '100%',
  padding: '16px 20px 20px 20px', // Add padding back for material grids
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)', // Keep 3 columns on mobile too
    gap: '10px',
    padding: '12px 16px 16px 16px',
  },
}))

const AssetItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: '100%', // Use full width of grid column
  aspectRatio: '1', // Maintain square shape
  height: 'auto',
  borderRadius: '8px',
  border: isActive
    ? `2px solid ${PURPLE_THEME.primary}`
    : `2px solid ${PURPLE_THEME.primaryBorder}`,
  backgroundColor: PURPLE_THEME.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: isActive ? PURPLE_THEME.primaryHover : PURPLE_THEME.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(PURPLE_THEME.primary, 0.15)}`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  '& img': {
    width: '40px',
    height: '40px',
    borderRadius: '6px',
  },
}))

const MaterialItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: '100%', // Use full width of grid column
  aspectRatio: '1', // Maintain square shape
  height: 'auto',
  borderRadius: '12px',
  border: isActive
    ? `2px solid ${PURPLE_THEME.primary}`
    : `2px solid ${PURPLE_THEME.primaryBorder}`,
  backgroundColor: PURPLE_THEME.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: isActive ? PURPLE_THEME.primaryHover : PURPLE_THEME.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 16px ${alpha(PURPLE_THEME.primary, 0.15)}`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  '& img': {
    width: '72px',
    height: '72px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
}))

const ColorItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'bgcolor',
})<{ isActive?: boolean; bgcolor?: string }>(({ theme, isActive, bgcolor }) => ({
  width: '100%', // Use full width of grid column
  aspectRatio: '1', // Maintain square shape
  height: 'auto',
  borderRadius: '8px',
  border: isActive
    ? `3px solid ${PURPLE_THEME.primary}`
    : `2px solid ${PURPLE_THEME.primaryBorder}`,
  backgroundColor: bgcolor || PURPLE_THEME.primaryBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: isActive ? PURPLE_THEME.primaryHover : PURPLE_THEME.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(PURPLE_THEME.primary, 0.15)}`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
}))

const TextButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}))

const ModernTextButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '12px 16px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  justifyContent: 'flex-start',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    borderColor: '#3b82f6',
    color: '#3b82f6',
  },
}))

interface Props {
  defaultSelectedCategory: string
  selectedAssets: PatternSelections
  topicType: string
  searchTerm?: string // Add search functionality
  onAssetSelected: (category: string, assets: PatternSelections) => void
  onTabSelected: (category: string) => void
  onAddTextElement: (type: number) => void
  onTemplateChanged: () => void
  onAccentChanged: (accentID: number) => void
  accentSlidersData: AccentSlidersData
  onUpdateAccentSliderData: (value: AccentSlidersData) => void
  printTiling: number
  onUpdatePrintTilingData: (value: number) => void
}

export default function ModernAssetSelector({
  defaultSelectedCategory = '',
  selectedAssets,
  topicType,
  searchTerm = '',
  onAssetSelected,
  onTabSelected,
  onAddTextElement,
  onTemplateChanged,
  onAccentChanged,
  accentSlidersData = { scale: 0, tiling: 0, rotation: 0.001 },
  onUpdateAccentSliderData,
  printTiling = 0,
  onUpdatePrintTilingData,
}: Props) {
  const [category, setCategory] = useState(defaultSelectedCategory)
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState(TemplateCategories[0])
  const [openColorPicker, setOpenColorPicker] = useState(false)
  const [selectedColors, setSelectedColors] = useState<string[]>([])
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ewUser] = useLocalStorage('ewUser', null) as [any, unknown]

  const { isPro } = useSubscription(ewUser?.userID)
  const isPremium = isPro

  // Only show upgrade CTAs on localhost (for testing)
  const isLocalhost = isDebugMode()

  // Search filtering function
  const filterAssets = (assets: any[], searchTerm: string) => {
    if (!searchTerm.trim()) return assets

    const term = searchTerm.toLowerCase().trim()
    return assets.filter((asset) => {
      // Search in asset name
      if (asset.name && typeof asset.name === 'string' && asset.name.toLowerCase().includes(term))
        return true

      // Search in asset ID/filename (convert to string first)
      if (asset.id && String(asset.id).toLowerCase().includes(term)) return true

      // Search in asset thumb path for keywords
      if (
        asset.thumb &&
        typeof asset.thumb === 'string' &&
        asset.thumb.toLowerCase().includes(term)
      )
        return true

      // Search in hex values for colors
      if (asset.hex && Array.isArray(asset.hex)) {
        return asset.hex.some(
          (color: any) => typeof color === 'string' && color.toLowerCase().includes(term),
        )
      }

      return false
    })
  }

  const { dialog: WarningTemplateDialog, confirm: warningTemplate } = useConfirm({
    title: 'Changing Template?',
    message:
      'Are you sure you want to change your template? You will lose all changes and reset entire note details with new template details',
    destructive: true,
  })
  const { dialog: WarningApplyMattingDialog, confirm: warningApplyMatting } = useConfirm({
    title: 'Template Required',
    message:
      'Please ensure that a template is applied before adding matting, as matting requires an active template to function correctly.',
    singleAction: true,
  })

  useEffect(() => {
    if (defaultSelectedCategory && defaultSelectedCategory !== category) {
      setCategory(defaultSelectedCategory)
    }
  }, [defaultSelectedCategory])

  const onPressCategory = (ctgy: string) => {
    setCategory(ctgy)
    onTabSelected(ctgy)
    SetCurrentCategory(ctgy)
  }

  const onSelectAsset = async (id: string | number) => {
    let updated: PatternSelections = { ...selectedAssets }
    const c = category === 'album' || category === 'upload' ? 'print' : category
    console.log('category: ', category)
    console.log('id: ', id)
    console.log('Selected accent: ', selectedAssets.accent)

    if (
      c === 'accent' &&
      selectedAssets.accent !== id &&
      selectedAssets.accent !== null &&
      selectedAssets.accent !== undefined
    ) {
      const ans = await warningTemplate()
      if (!ans) return
      else {
        onAccentChanged(Number(id))
        return
      }
    }

    const currentValue = (updated as any)[c]
    const newValue = currentValue === id ? undefined : id

    updated = {
      ...updated,
      [c]: newValue,
    }

    if ((c === 'frame' || c === 'patternFrame') && !newValue) {
      console.log('🖼️ Frame deselected. Resetting matting to 0.')
      updated.matting.width = 0
    }

    // Keep the actual category (upload/album) when calling onAssetSelected to prevent tab switching
    onAssetSelected(category, updated)
  }

  const handleBoarderChange = (type: string) => (event: SelectChangeEvent) => {
    onAssetSelected(category, {
      ...selectedAssets,
      [category]: {
        ...(selectedAssets as any)[category],
        [type]: event.target.value,
      },
    })
  }

  const handleTemplateSelectChange = (type: string) => (event: SelectChangeEvent) => {
    setSelectedTemplateCategory(event.target.value)
  }

  const removeCustomIdentifier = (color: string | undefined | null) => {
    if (!color) return ''
    return color.includes('-custom') ? color.replace('-custom', '') : color
  }

  const getCategoryTitle = (cat: string) => {
    switch (cat) {
      case 'patternFrame':
        return 'Frame'
      case 'template':
        return 'Templates'
      case 'material':
        return 'Materials'
      case 'print':
        return 'Prints'
      case 'color':
        return 'Colors'
      case 'accent':
        return 'Template' // Renamed from 'Accents' to 'Template'
      case 'frame':
        return 'Frames'
      case 'scene':
        return 'Scenes'
      case 'text':
        return 'Text Elements'
      case 'matting':
        return 'Matting'
      case 'boarder':
        return 'Border'
      case 'album':
        return 'Albums'
      case 'upload':
        return 'Uploads'
      case 'settings':
        return 'Settings'
      default:
        return cat
    }
  }

  const getCategorySubtitle = (cat: string) => {
    switch (cat) {
      case 'template':
        return 'Choose a layout template'
      case 'material':
        return 'Select material textures'
      case 'print':
        return 'Add your images'
      case 'color':
        return 'Pick colors and gradients'
      case 'accent':
        return 'Choose your template design'
      case 'frame':
        return 'Frame your design'
      case 'scene':
        return 'Set the environment'
      case 'text':
        return 'Add text elements'
      case 'matting':
        return 'Configure matting size'
      case 'boarder':
        return 'Adjust border settings'
      case 'album':
        return 'Browse your albums'
      case 'upload':
        return 'Upload new images'
      case 'settings':
        return 'Adjust design settings'
      default:
        return 'Select an option'
    }
  }

  const renderAssetItem = (item: IPatternAsset) => {
    const selectedId = String((selectedAssets as any)[category])

    if (category === 'material') {
      const asset = item as IPatternAssetMaterial
      return (
        <MaterialItem
          key={asset.id}
          isActive={selectedId === String(asset.id)}
          onClick={() => onSelectAsset(asset.id)}
        >
          <img src={`/assets/patterns/${asset.thumb}`} alt={item.name} />
        </MaterialItem>
      )
    }

    if (category === 'color') {
      const asset = item as IPatternAssetColor
      return (
        <ColorItem
          key={asset.hex[0]}
          isActive={selectedId === asset.hex[0]}
          bgcolor={asset.hex[0]}
          onClick={() => onSelectAsset(asset.hex[0])}
        />
      )
    }

    // Default template/accent/frame/scene items
    const asset = item as IPatternAssetTemplate
    return (
      <AssetItem
        key={asset.id}
        isActive={selectedId === String(asset.id)}
        onClick={() => onSelectAsset(asset.id)}
      >
        <img
          src={`/assets/patterns/${asset.thumb}`}
          alt={item.name}
          style={{
            backgroundColor: category === 'accent' ? '#9ca3af' : 'transparent',
            padding: category === 'accent' ? '2px' : '0',
          }}
        />
      </AssetItem>
    )
  }

  const renderContent = () => {
    if (category === 'template') {
      let templates: IPatternAsset[] = []
      if (topicType === TopicType.GALLERY.toString()) {
        templates = TemplateSets.GalleryTemplates
      } else if (topicType === TopicType.QUILT.toString()) {
        templates = TemplateSets.QuiltTemplates
      } else {
        templates = TemplateSets.PatternTemplates
      }

      const filteredTemplates = filterAssets(templates, searchTerm)

      if (searchTerm && filteredTemplates.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No templates found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      return <AssetGrid>{filteredTemplates.map((item) => renderAssetItem(item))}</AssetGrid>
    }

    if (category === 'material') {
      const filteredMaterials = filterAssets(Assets.materials, searchTerm)

      if (searchTerm && filteredMaterials.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No materials found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      const showUpgrade =
        isLocalhost && !isPremium && filteredMaterials.length > FREE_TIER_LIMITS.material
      const displayedMaterials = showUpgrade
        ? filteredMaterials.slice(0, FREE_TIER_LIMITS.material)
        : filteredMaterials
      const lockedCount = filteredMaterials.length - FREE_TIER_LIMITS.material

      return (
        <>
          <MaterialGrid sx={{ paddingBottom: showUpgrade ? '0px' : '20px' }}>
            {displayedMaterials.map((item) => renderAssetItem(item))}
          </MaterialGrid>
          {showUpgrade && (
            <Box sx={{ padding: '16px 20px 20px 20px' }}>
              <UpgradeCard
                variant='banner'
                title='Unlock All Premium Materials'
                description='Get unlimited access to all premium materials, textures, and patterns. Create professional designs without limitations.'
                itemCount={lockedCount}
                onUpgrade={() => setPricingModalOpen(true)}
              />
            </Box>
          )}
        </>
      )
    }

    if (category === 'color') {
      const selectedId = String((selectedAssets as any)[category])
      const filteredColors = filterAssets(Assets.colors, searchTerm)

      if (searchTerm && filteredColors.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No colors found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      return (
        <AssetGrid>
          <ColorItem
            isActive={selectedId === selectedColors[0]}
            bgcolor={removeCustomIdentifier(selectedColors[0]) || '#e5e7eb'}
            onClick={() => onSelectAsset(selectedColors[0] || '#e0e0e0')}
          >
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                setOpenColorPicker(true)
              }}
              sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.3)' }}
            >
              <Colorize fontSize='small' />
            </IconButton>
          </ColorItem>
          {filteredColors.map((item, index) => (
            <React.Fragment key={`color-${index}`}>{renderAssetItem(item)}</React.Fragment>
          ))}
          <ColorPickerDialog
            open={openColorPicker}
            onClose={() => setOpenColorPicker(false)}
            onColorSelect={(newColor) => {
              const modifiedColors = [...newColor]
              modifiedColors[0] = `${newColor[0]}-custom`
              setSelectedColors(modifiedColors)
              UpdateAddColorHex(modifiedColors)
              onSelectAsset(modifiedColors[0])
            }}
            selectedColors={selectedColors}
          />
        </AssetGrid>
      )
    }

    if (category === 'accent') {
      const filteredAccents = filterAssets(Assets.accents, searchTerm)

      if (searchTerm && filteredAccents.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No accents found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      const showUpgrade =
        isLocalhost && !isPremium && filteredAccents.length > FREE_TIER_LIMITS.accent
      const displayedAccents = showUpgrade
        ? filteredAccents.slice(0, FREE_TIER_LIMITS.accent)
        : filteredAccents
      const lockedCount = filteredAccents.length - FREE_TIER_LIMITS.accent

      return (
        <>
          <AssetGrid sx={{ paddingBottom: showUpgrade ? '0px' : '20px' }}>
            {displayedAccents.map((item) => renderAssetItem(item))}
          </AssetGrid>
          {showUpgrade && (
            <Box sx={{ padding: '16px 20px 20px 20px' }}>
              <UpgradeCard
                variant='banner'
                title='Unlock All Premium Templates'
                description='Access hundreds of premium template designs. Take your designs to the next level with professional templates.'
                itemCount={lockedCount}
                onUpgrade={() => setPricingModalOpen(true)}
              />
            </Box>
          )}
        </>
      )
    }

    if (category === 'frame') {
      const filteredFrames = filterAssets(Assets.frames, searchTerm)

      if (searchTerm && filteredFrames.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No frames found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      return <AssetGrid>{filteredFrames.map((item) => renderAssetItem(item))}</AssetGrid>
    }

    if (category === 'patternFrame') {
      const filteredPatternFrames = filterAssets(Assets.patternFrames, searchTerm)

      if (searchTerm && filteredPatternFrames.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No pattern frames found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      return <AssetGrid>{filteredPatternFrames.map((item) => renderAssetItem(item))}</AssetGrid>
    }

    if (category === 'scene') {
      const filteredScenes = filterAssets(Assets.scenes, searchTerm)

      if (searchTerm && filteredScenes.length === 0) {
        return (
          <Box sx={{ padding: '40px 20px', textAlign: 'center' }}>
            <Typography variant='body2' color='textSecondary'>
              No scenes found for "{searchTerm}"
            </Typography>
          </Box>
        )
      }

      return <AssetGrid>{filteredScenes.map((item) => renderAssetItem(item))}</AssetGrid>
    }

    if (category === 'text') {
      return (
        <TextButtonGroup>
          <ModernTextButton onClick={() => onAddTextElement(1)}>Add Heading</ModernTextButton>
          <ModernTextButton onClick={() => onAddTextElement(2)}>Add Subheading</ModernTextButton>
          <ModernTextButton onClick={() => onAddTextElement(3)}>Add Body Text</ModernTextButton>
        </TextButtonGroup>
      )
    }

    // if (category === 'matting') {
    //   const { width } = (selectedAssets as any)[category]
    //   return (
    //     <FormSection>
    //       <FormLabel>Matting Size</FormLabel>
    //       <FormControl fullWidth>
    //         <StyledSelect value={width} onChange={handleMattingChange('width') as any}>
    //           {MattingWidthRange.map((w) => (
    //             <MenuItem value={w} key={w}>
    //               {w}
    //             </MenuItem>
    //           ))}
    //         </StyledSelect>
    //       </FormControl>
    //     </FormSection>
    //   )
    // }

    if (category === 'boarder') {
      const { width, type } = (selectedAssets as any)[category]
      return (
        <Box>
          <FormSection>
            <FormLabel>Border Size</FormLabel>
            <FormControl fullWidth>
              <StyledSelect value={width} onChange={handleBoarderChange('width') as any}>
                {BoarderWidthRange.map((w) => (
                  <MenuItem value={w} key={w}>
                    {w}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </FormSection>
          <FormSection>
            <FormLabel>Border Type</FormLabel>
            <FormControl fullWidth>
              <StyledSelect value={type} onChange={handleBoarderChange('type') as any}>
                {BoarderTypes.map((t) => (
                  <MenuItem value={t} key={t}>
                    {t.toUpperCase()}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </FormSection>
        </Box>
      )
    }

    if (category === 'print') {
      const selectedId = String((selectedAssets as any)[category])
      return (
        <ModernPrintTabView
          showCustomPrints={topicType !== TopicType.CUSTOM_PRINTS.toString()}
          selectedId={selectedId}
          onSelect={onSelectAsset}
          isPremium={isPremium}
          freeLimit={FREE_TIER_LIMITS.print}
        />
      )
    }

    if (category === 'upload') {
      const selectedID = String((selectedAssets as any)['print'])
      return <UploadsList selectedAssetID={selectedID} onSelectAsset={onSelectAsset} />
    }

    if (category === 'album') {
      const selectedID = String((selectedAssets as any)['print'])
      return <AlbumList selectedAssetID={selectedID} onSelectAsset={onSelectAsset} />
    }

    // if (category === 'settings') {
    //   return (
    //     <ModernSettingsTabView
    //       scale={accentSlidersData.scale}
    //       onUpdateScale={(value) =>
    //         onUpdateAccentSliderData({ ...accentSlidersData, scale: value })
    //       }
    //       tiling={accentSlidersData.tiling}
    //       onUpdateTiling={(value) =>
    //         onUpdateAccentSliderData({ ...accentSlidersData, tiling: value })
    //       }
    //       rotation={accentSlidersData.rotation}
    //       onUpdateRotation={(value) =>
    //         onUpdateAccentSliderData({ ...accentSlidersData, rotation: value })
    //       }
    //       printTiling={printTiling}
    //       onUpdatePrintTiling={onUpdatePrintTilingData}
    //       scene={GetSceneObject()}
    //     />
    //   )
    // }

    return null
  }

  const availableCategories = PatternAssetsMap[topicType] || []
  const availableTabs = PatternAssetTabs[topicType] || []

  return (
    <ModernSidebarContainer>
      <CategoryNavigation>
        {availableCategories.map(({ categoryName, icon }) => (
          <Tooltip key={categoryName} title={getCategoryTitle(categoryName)} placement='right'>
            <CategoryButton
              isActive={category === categoryName}
              onClick={() => onPressCategory(categoryName)}
            >
              <Box display='flex' flexDirection='column' alignItems='center'>
                <SVG
                  src={icon}
                  width={24}
                  height={24}
                  color={
                    category === categoryName ? PURPLE_THEME.white : PURPLE_THEME.textSecondary
                  }
                />
                <CategoryLabel>
                  {categoryName === 'patternFrame'
                    ? 'Frame'
                    : categoryName === 'accent'
                      ? 'Template'
                      : categoryName}
                </CategoryLabel>
              </Box>
            </CategoryButton>
          </Tooltip>
        ))}

        {/* Hide albums and uploads buttons from toolbar - commented out, not removed */}
        {availableTabs
          .filter((tab) => tab.key !== 'album' && tab.key !== 'upload')
          .map((tab) => (
            <Tooltip key={tab.key} title={getCategoryTitle(tab.key)} placement='right'>
              <CategoryButton
                isActive={category === tab.key}
                onClick={() => onPressCategory(tab.key)}
              >
                <Box display='flex' flexDirection='column' alignItems='center'>
                  <SVG
                    src={tab.icon}
                    width={24}
                    height={24}
                    color={category === tab.key ? PURPLE_THEME.white : PURPLE_THEME.textSecondary}
                  />
                  <CategoryLabel>{tab.title || getCategoryTitle(tab.key)}</CategoryLabel>
                </Box>
              </CategoryButton>
            </Tooltip>
          ))}
      </CategoryNavigation>

      <ContentArea>
        <ContentHeader>
          <ContentTitle>{getCategoryTitle(category)}</ContentTitle>
          <ContentSubtitle>{getCategorySubtitle(category)}</ContentSubtitle>
          {(category == 'template' || category == 'accent') && (
            <FormControl sx={{ mt: '8px' }} fullWidth>
              <StyledSelect
                value={selectedTemplateCategory}
                onChange={handleTemplateSelectChange('cat') as any}
              >
                {TemplateCategories.map((w) => (
                  <MenuItem value={w} key={w}>
                    {w}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          )}
        </ContentHeader>

        <ScrollableContent>{renderContent()}</ScrollableContent>
      </ContentArea>

      <WarningTemplateDialog />
      <WarningApplyMattingDialog />

      {/* Only show pricing modal on localhost */}
      {isLocalhost && (
        <PricingModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} userId={ewUser?.userID} userEmail={ewUser?.userName} />
      )}
    </ModernSidebarContainer>
  )
}
